//@flow
import * as React from 'react';
import { type TFunction } from 'react-i18next';
import { List, Map } from 'immutable';

import { type FpPjPlan } from 'common/models/internal/FpPjPlan';
import { type FpBeat } from 'common/models/internal/FpBeat';

import FpPlanMap from './FpPlanMap';
import FpPlanChartView from './FpPlanChartView';
import { MarkerTypeEnum } from 'common/enums/MapEnum';
import ZoomLevelChanger from 'common/components/common/ZoomLevelChanger';

import uuidv4 from 'uuid/v4';
import DifferentiableColorPicker from 'common/utils/DifferentiableColorPicker';

import { FpContainers } from 'common/entity/pjpMaster/planView/FpPlanDetailsContainer';

import baseFpPlanViewFactory from './baseFpPlanViewFactory';
import FpPlanViewActionsBar from './FpPlanViewActionsBar';

import { SortPref, SortingOptions } from './FpBeatSort';

import isEqual from 'lodash/isEqual';

import { type SalesCategoryModelType } from 'common/models/flow/SalesCategory';
import type { VisitIdOutletType } from 'common/entity/pjpMaster/planView/FpPlanDetailsContainer';
import { type FpVisitDays } from 'common/models/internal/FpOutletVisit';
import { type FpVisitAddress } from 'common/models/flow/FpVisitAddress';
import { type FpSalesbeatPlan } from 'common/models/internal/FpSalesbeatPlan';
import { FpMapColorByOptions } from './FpPlanMapOptions';

const FpAllocatedVisitsView = baseFpPlanViewFactory({
    ChartView: FpPlanChartView,
    MapView: FpPlanMap,
    ActionsBar: FpPlanViewActionsBar,
    ZoomSlider: ZoomLevelChanger
});

export type FpAllocatedMapOptionsType = {
    aerialRoute: boolean,
    colorBy: boolean,
    clusters: boolean,
    markerType: string,
    polygon: boolean,
    salesCategory: boolean,
    showMarkers: boolean,
    showUnassignedMarkers: boolean
};

export type UnallocatedVisitType = {
    id: string,
    sourceOutletId: string,
    zoneId: ?string,
    category: string,
    visitType: string,
    address: FpVisitAddress,
    revenue: {
        amount: ?number,
        currency: ?string,
        symbol: ?string
    },
    possibleDays: List<FpVisitDays>,
    isUnallocated: boolean
};

export type FpPlanViewPagination = {
    pageSize: number,
    totalItems: number,
    pageSizeOptions: Array<number>,
    totalPages: number,
    selectedPage: number,
    selectedPageIndex: number
};

type State = {
    pjPlan: FpPjPlan,
    salesbeatPlan: FpSalesbeatPlan,
    beats: List<FpBeat>,
    isMoveVisitDialogVisible: boolean,
    paginationDetails: FpPlanViewPagination,
    mapOptions: FpAllocatedMapOptionsType,
    visibleBeats: List<FpBeat>,
    filteredBeats: List<FpBeat>,
    beatSelectionMap: Map<string, boolean>,
    selectedVisit: ?Object,
    contextMenuOpen: boolean,
    contextMenu: Object,
    selectedBeat: ?string,
    selectedSortOption: ?string,
    selectedSortOrder: ?string,
    selectedFilters: Map<string, Array<Object>>,
    currentHoveredVisit: ?string,
    hoveredBeat: ?string,
    salesCategoryColorMap: Map<string, { code: string }>,
    riderIdColorMap: Map<string, { code: string }>,
    weekColorMap: Map<string, { code: string }>,
    weekDayColorMap: Map<string, { code: string }>,
    selectAll: boolean,
    status: string,
    hoveredUnallocatedVisit: ?string,
    selectedUnallocatedVisit: ?string,
    targetVisitId: ?string,
    originalBeatId: ?string,
    unallocatedVisits: List<UnallocatedVisitType>,
    visibleBeatColorMap: Map<string, Object>
};

export const PolygonOptionTypes = Object.freeze({
    ALL: 'all',
    SELECTED: 'selected',
    FILTERED: 'filtered'
});
type Props = {
    plan: FpPjPlan,
    salesbeatPlan: FpSalesbeatPlan,
    outletSalesCategories: Array<SalesCategoryModelType>,
    visitIdMap: Map<string, VisitIdOutletType>,
    moveVisit: (
        clientId: string,
        targetBeatId: string,
        visitId: string,
        pjpId: string,
        id: string
    ) => Promise<any>,
    reloadData: () => void,
    unallocateVisit: (
        clientId: string,
        visitId: string,
        pjpId: string,
        id: string
    ) => Promise<any>,
    t: TFunction
};

class FpAllocatedVisitsContainer extends React.Component<Props, State> {
    planMapWrapRef: ?typeof PlanMapWrap;
    allocatedContainerRef: React.ElementRef<any>;

    constructor(props: Props) {
        super(props);
        const { plan, salesbeatPlan } = props;
        const paginationDetails = this.getDefaultPaginationDetails(
            salesbeatPlan.beats
        );
        const visibleBeats = this.getVisibleBeats(
            salesbeatPlan.beats,
            paginationDetails
        );
        this.state = {
            pjPlan: plan,
            salesbeatPlan,
            status: plan.planStatus,
            beats: salesbeatPlan.beats,
            isMoveVisitDialogVisible: false,
            paginationDetails,
            mapOptions: this.getDefaultMapOptions(),
            visibleBeats,
            filteredBeats: salesbeatPlan.beats,
            beatSelectionMap: this.getBeatSelectionMap(visibleBeats),
            selectedVisit: null,
            contextMenuOpen: false,
            contextMenu: null,
            selectedBeat: null,
            selectedSortOption: null,
            selectedSortOrder: null,
            salesCategoryColorMap: Map(),
            riderIdColorMap: this.getRiderIdColorMap(plan),
            weekColorMap: this._getWeekColorMap(plan),
            weekDayColorMap: this._getWeekDayColorMap(plan),
            selectedFilters: Map({
                salesman: [],
                salesCategories: [],
                weeks: [],
                days: []
            }),
            currentHoveredVisit: null,
            hoveredBeat: null,
            selectAll: false,
            selectedUnallocatedVisit: null,
            hoveredUnallocatedVisit: null,
            unallocatedVisits: this.createUnallocatedVisits(
                plan,
                salesbeatPlan
            ),
            targetVisitId: null,
            visibleBeatColorMap: this._getDefaultBeatColorMapping(
                salesbeatPlan.beats
            ),
            sortingField: null,
            sortingOrder: null,
            originalBeatId: null
        };
    }

    componentDidMount() {
        const { outletSalesCategories } = this.props;
        const colorPicker = new DifferentiableColorPicker();
        const salesCategoryColorMap = outletSalesCategories.reduce(
            (salesCategoryMap, salesCategory) => {
                if (!salesCategoryMap.has(salesCategory.id)) {
                    const updatedMap = salesCategoryMap.set(
                        salesCategory.id,
                        colorPicker.pick()
                    );
                    return updatedMap;
                }
                return salesCategoryMap;
            },
            Map()
        );

        this.setState({
            salesCategoryColorMap
        });
    }

    componentDidUpdate(prevProps: Props) {
        if (!isEqual(prevProps.salesbeatPlan, this.props.salesbeatPlan)) {
            this.setState(
                {
                    beats: this.props.salesbeatPlan.beats,
                    filteredBeats: this.props.salesbeatPlan.beats
                },
                () => {
                    const allFilters = this.getAllFilters();
                    const {
                        selectedFilters,
                        selectedSortOption,
                        selectedSortOrder,
                        paginationDetails
                    } = this.state;
                    if (
                        (selectedSortOption && selectedSortOrder) ||
                        allFilters.length
                    ) {
                        if (selectedSortOption && selectedSortOrder) {
                            this.sortBeats(
                                selectedSortOption,
                                selectedSortOrder
                            );
                        }
                        if (allFilters.length) {
                            this.applyFilters(
                                selectedFilters.get('days'),
                                selectedFilters.get('weeks'),
                                selectedFilters.get('salesCategories'),
                                selectedFilters.get('salesman')
                            );
                        }
                    } else {
                        const { salesbeatPlan } = this.props;
                        const visibleBeats = this.getVisibleBeats(
                            salesbeatPlan.beats,
                            paginationDetails
                        );
                        this.setState({
                            visibleBeats,
                            salesbeatPlan,
                            unallocatedVisits: this.createUnallocatedVisits(
                                this.props.plan,
                                salesbeatPlan
                            )
                        });
                    }
                }
            );
        }
    }

    componentWillUnmount() {
        this.onVisitHoverOutside();
        this.onVisitDeselect();
    }

    getRiderIdColorMap = pjPlan => {
        const { riderIds } = pjPlan.ridersInfo;
        const riderColorPicker = new DifferentiableColorPicker();
        const riderIdColorMap = riderIds.reduce((riderColorMap, riderId) => {
            if (!riderColorMap.has(riderId)) {
                const updatedRiderColorMap = riderColorMap.set(
                    riderId.riderId,
                    riderColorPicker.pick()
                );
                return updatedRiderColorMap;
            }
            return riderColorMap;
        }, Map());
        return riderIdColorMap;
    };

    _getWeekDayColorMap = pjpPlan => {
        const { beats } = pjpPlan.salesbeatPlan;
        const weekColorPicker = new DifferentiableColorPicker();
        const weekColorMap = beats.reduce((weekColorMap, { beatDay }) => {
            if (!weekColorMap.has(beatDay.dayOfWeek)) {
                const updatedWeekColorMap = weekColorMap.set(
                    beatDay.dayOfWeek,
                    weekColorPicker.pick()
                );
                return updatedWeekColorMap;
            }
            return weekColorMap;
        }, Map());
        return weekColorMap;
    };

    _getWeekColorMap = pjpPlan => {
        const { beats } = pjpPlan.salesbeatPlan;
        const weekColorPicker = new DifferentiableColorPicker();
        const weekColorMap = beats.reduce((weekColorMap, { beatDay }) => {
            if (!weekColorMap.has(beatDay.week)) {
                const updatedWeekColorMap = weekColorMap.set(
                    beatDay.week,
                    weekColorPicker.pick()
                );
                return updatedWeekColorMap;
            }
            return weekColorMap;
        }, Map());
        return weekColorMap;
    };

    createUnallocatedVisits = (
        plan: FpPjPlan,
        salesbeatPlan: FpSalesbeatPlan
    ) => {
        const { unallocatedVisitIds } = salesbeatPlan;
        const { outletVisits } = plan;
        const unallocatedVisits = unallocatedVisitIds.map(unallocatedVisit => {
            const outletData = outletVisits.find(visit => {
                return visit.id === unallocatedVisit;
            });
            const {
                id,
                sourceOutletId,
                zoneId,
                category,
                visitType,
                address,
                revenue,
                possibleDays
            } = outletData;
            return {
                id,
                sourceOutletId,
                zoneId,
                category,
                visitType,
                address,
                revenue,
                possibleDays,
                isUnallocated: true
            };
        });
        return unallocatedVisits;
    };

    _getDefaultBeatColorMapping(beats) {
        const colorPicker = new DifferentiableColorPicker();
        return beats.reduce((colorMap, beat) => {
            const updatedColorMap = colorMap.set(
                beat.beatId,
                colorPicker.pick()
            );
            return updatedColorMap;
        }, Map());
    }

    resetSelectAll() {
        const { beats, beatSelectionMap } = this.state;
        let updatedBeatSelectionMap = Map(beatSelectionMap);
        beats.forEach(beat => {
            updatedBeatSelectionMap = updatedBeatSelectionMap.set(
                beat.beatId,
                false
            );
        });
        this.setState({
            beatSelectionMap: updatedBeatSelectionMap,
            selectAll: false
        });
    }

    onSelectAllToggle: (checked: boolean) => void = (checked: boolean) => {
        const { visibleBeats, beatSelectionMap } = this.state;
        let updatedBeatSelectionMap = Map(beatSelectionMap);
        //@refactor this part
        visibleBeats.forEach(beat => {
            updatedBeatSelectionMap = updatedBeatSelectionMap.set(
                beat.beatId,
                checked
            );
        });

        this.setState({
            beatSelectionMap: updatedBeatSelectionMap,
            selectAll: checked
        });
    };

    PAGE_SIZE_OPTIONS: Array<number> = [10, 15, 25];

    onUnallocatedVisitSelect = (visitId: string) => {
        this.setState({
            selectedUnallocatedVisit: visitId,
            currentHoveredVisit: null,
            hoveredBeat: null
        });
    };
    onUnallocatedVisitDeselect = () => {
        this.setState({
            selectedUnallocatedVisit: null
        });
    };
    onUnallocatedVisitHover = (visitId: string) => {
        this.setState({
            hoveredUnallocatedVisit: visitId,
            currentHoveredVisit: null,
            hoveredBeat: null
        });
    };

    onUnallocatedVisitHoverOut = () => {
        this.setState({
            hoveredUnallocatedVisit: null
        });
    };

    onVisitHover = (beatId: string, visitId: string) => {
        const { beatSelectionMap, selectedVisit, selectedBeat } = this.state;
        if (selectedVisit && selectedBeat) {
            return;
        } else if (beatSelectionMap.get(beatId) && !selectedVisit) {
            this.setState({
                currentHoveredVisit: visitId,
                hoveredBeat: beatId
            });
        }
    };

    onVisitHoverOutside = () => {
        this.setState({
            currentHoveredVisit: null,
            hoveredBeat: null
        });
    };

    onVisitClick = (beatId: string, visitId: string) => {
        const { visibleBeats, beatSelectionMap } = this.state;
        if (beatSelectionMap.get(beatId)) {
            const selectedBeat = visibleBeats.find(beat => {
                return beat.beatId === beatId;
            });
            if (selectedBeat) {
                const selectedVisit = selectedBeat.visits.find(visit => {
                    return (
                        visit.outletVisitId === visitId || visit.id === visitId
                    );
                });
                this.setState({
                    selectedVisit: selectedVisit?.outletVisitId || visitId,
                    selectedBeat: beatId
                });
            }
        }
    };

    onVisitDeselect = () => {
        this.setState({ selectedVisit: null, selectedBeat: null });
    };

    getBeatSelectionMap = (beats: List<FpBeat>) => {
        const beatSelectionMap = beats.reduce((acc, beat) => {
            const updatedAcc = acc.set(beat.beatId, false);
            return updatedAcc;
        }, Map());
        return beatSelectionMap;
    };

    toggleBeatSelection = (beatId: string) => {
        const { beatSelectionMap } = this.state;
        const isBeatSelected = beatSelectionMap.get(beatId);
        const updatedBeatSelectionMap = beatSelectionMap.set(
            beatId,
            !isBeatSelected
        );
        this.setState({
            beatSelectionMap: updatedBeatSelectionMap
        });
    };

    getVisibleBeats = (
        beats: List<FpBeat>,
        paginationDetails: {
            pageSize: number,
            pageSizeOptions: Array<number>,
            selectedPage: number,
            selectedPageIndex: number,
            totalItems: number,
            totalPages: number
        }
    ) => {
        const { selectedPageIndex, pageSize } = paginationDetails;
        const startIndex = pageSize * selectedPageIndex;
        const visibleBeats = beats.filter((beat, index) => {
            return index >= startIndex && index < startIndex + pageSize;
        });
        return visibleBeats;
    };

    getDefaultPaginationDetails: (
        beats: List<FpBeat>
    ) => {
        pageSize: number,
        pageSizeOptions: Array<number>,
        selectedPage: number,
        selectedPageIndex: number,
        totalItems: number,
        totalPages: number
    } = beats => {
        const totalPages = Math.ceil(beats.length / 10);
        return {
            pageSize: 10,
            totalItems: beats.length,
            totalPages,
            selectedPage: 1,
            selectedPageIndex: 0,
            pageSizeOptions: [10, 20, 30]
        };
    };

    getDefaultMapOptions = () => {
        return {
            showMarkers: true,
            markerType: MarkerTypeEnum.Numbered,
            aerialRoute: true,
            polygonOption: PolygonOptionTypes.ALL,
            colorBy: false,
            salesCategory: false,
            polygon: true,
            showUnassignedMarkers: false,
            colorByType: FpMapColorByOptions.SalesCategory
        };
    };

    onOptionsChange = (updatedMapOptions: FpAllocatedMapOptionsType) => {
        this.setState({ mapOptions: updatedMapOptions });
    };

    sortBeats = (sortingField: string, sortingOrder: string) => {
        const { filteredBeats, paginationDetails } = this.state;
        let sortedBeats = [...filteredBeats];
        const order = {
            SUNDAY: 1,
            MONDAY: 2,
            TUESDAY: 3,
            WEDNESDAY: 4,
            THURSDAY: 5,
            FRIDAY: 6,
            SATURDAY: 7
        };
        const weekOrder = {
            WEEK1: 1,
            WEEK2: 2,
            WEEK3: 3,
            WEEK4: 4,
            WEEK5: 5,
            WEEK6: 6,
            WEEK7: 7,
            WEEK8: 8,
            WEEK9: 9,
            WEEK10: 10,
            WEEK11: 11,
            WEEK12: 12
        };
        if (sortingField === SortingOptions.Week) {
            if (sortingOrder === SortPref.ASC) {
                sortedBeats = sortedBeats.sort((a, b) => {
                    return (
                        weekOrder[a.beatDay.week] - weekOrder[b.beatDay.week]
                    );
                });
            } else {
                sortedBeats = sortedBeats.sort((a, b) => {
                    return (
                        weekOrder[b.beatDay.week] - weekOrder[a.beatDay.week]
                    );
                });
            }
        }
        if (sortingField === SortingOptions.Day) {
            if (sortingOrder === SortPref.ASC) {
                sortedBeats = sortedBeats.sort((a, b) => {
                    return (
                        order[a.beatDay.dayOfWeek] - order[b.beatDay.dayOfWeek]
                    );
                });
            } else {
                sortedBeats = sortedBeats.sort((a, b) => {
                    return (
                        order[b.beatDay.dayOfWeek] - order[a.beatDay.dayOfWeek]
                    );
                });
            }
        }
        if (sortingField === SortingOptions.SalesCategory) {
            if (sortingOrder === SortPref.ASC) {
                sortedBeats = sortedBeats.sort((a, b) => {
                    if (a.categories[0] > b.categories[0]) return 1;
                    if (a.categories[0] < b.categories[0]) return -1;
                    return 0;
                });
            } else {
                sortedBeats = sortedBeats.sort((a, b) => {
                    if (a.categories[0] > b.categories[0]) return -1;
                    if (a.categories[0] < b.categories[0]) return 1;
                    return 0;
                });
            }
        }
        if (sortingField === SortingOptions.Salesman) {
            if (sortingOrder === SortPref.ASC) {
                sortedBeats = sortedBeats.sort((a, b) => {
                    if (a.currentSalesmanId > b.currentSalesmanId) return 1;
                    if (a.currentSalesmanId < b.currentSalesmanId) return -1;
                    return 0;
                });
            } else {
                sortedBeats = sortedBeats.sort((a, b) => {
                    if (a.currentSalesmanId > b.currentSalesmanId) return -1;
                    if (a.currentSalesmanId < b.currentSalesmanId) return 1;
                    return 0;
                });
            }
        }
        const visibleBeats = this.getVisibleBeats(
            sortedBeats,
            paginationDetails
        );
        this.setState({
            sortingOrder,
            sortingField,
            filteredBeats: sortedBeats,
            visibleBeats,
            selectedSortOption: sortingField,
            selectedSortOrder: sortingOrder
        });
    };

    resetSort = () => {
        const { paginationDetails, filteredBeats, beats } = this.state;
        const sortedBeats = filteredBeats.sort((a, b) => {
            return a.name.localeCompare(b.name, 'en', {
                numeric: true
            });
        });
        const visibleBeats = this.getVisibleBeats(
            sortedBeats,
            paginationDetails
        );
        this.setState({
            filteredBeats: beats,
            visibleBeats,
            selectedSortOrder: null,
            selectedSortOption: null,
            sortingOrder: null,
            sortingField: null
        });
    };

    onUnallocatedVisitContextMenuOpen = (
        x: number,
        y: number,
        visitId: string
    ) => {
        const contextId = uuidv4();
        this.setState({
            contextMenuOpen: true,
            contextMenu: {
                id: contextId,
                x,
                y,
                isUnallocated: true,
                visitId
            }
        });
        return contextId;
    };

    onContextMenuOpenMap = (
        x: number,
        y: number,
        beatId: string,
        visitId: string
    ) => {
        const contextId = uuidv4();
        this.setState({
            contextMenuOpen: true,
            contextMenu: {
                id: contextId,
                x,
                y,
                beatId,
                visitId,
                isUnallocated: false
            }
        });
        return contextId;
    };

    onContextMenuOpen = (
        x: number,
        y: number,
        beatId: string,
        visitId: string
    ) => {
        const contextId = uuidv4();
        this.setState({
            contextMenuOpen: true,
            contextMenu: {
                id: contextId,
                x,
                y,
                beatId,
                visitId,
                isUnallocated: false
            },
            selectedVisit: visitId,
            selectedBeat: beatId
        });
        return contextId;
    };

    onContextMenuClose = () => {
        this.setState({
            contextMenuOpen: false,
            contextMenu: null
        });
    };

    getAllFilters = () => {
        const { selectedFilters } = this.state;
        const allFilters = [];
        if (selectedFilters) {
            selectedFilters.entrySeq().forEach(item => {
                if (item[1].length) {
                    item[1].forEach(selectedFilter => {
                        allFilters.push({
                            filter: item[0],
                            value: selectedFilter
                        });
                    });
                }
            });
        }
        return allFilters;
    };

    removeFilterItem = (filterItem: Object) => {
        const { selectedFilters } = this.state;
        const { filter, value } = filterItem;
        const selectedFilter = selectedFilters.get(filter);
        const updatedFilterValues = selectedFilter.filter(filterValue => {
            return filterValue.value !== value.value;
        });
        const updatedSelectedFilters = selectedFilters.set(
            filter,
            updatedFilterValues
        );
        this.applyFilters(
            updatedSelectedFilters.get('days'),
            updatedSelectedFilters.get('weeks'),
            updatedSelectedFilters.get('salesCategories'),
            updatedSelectedFilters.get('salesman')
        );
    };

    onClearFilter = () => {
        const { paginationDetails, beats } = this.state;
        const visibleBeats = this.getVisibleBeats(beats, paginationDetails);
        const selectedFilters = Map({
            days: [],
            weeks: [],
            salesCategories: [],
            salesman: []
        });
        const updatedPaginationDetails = this.getDefaultPaginationDetails(
            beats
        );
        this.setState({
            visibleBeats,
            filteredBeats: beats,
            selectedSortOrder: null,
            selectedSortOption: null,
            selectedFilters,
            paginationDetails: updatedPaginationDetails
        });
    };

    applyFilters = (
        selectedDays: Array<string>,
        selectedWeeks: Array<string>,
        selectedSalesCategories: Array<string>,
        selectedSalesman: Array<string>
    ) => {
        const { beats } = this.state;
        let filteredBeats = [...beats];
        if (selectedSalesman.length) {
            filteredBeats = filteredBeats.filter(beat => {
                const beatSalesman = beat.currentSalesmanId;
                let isBeatFiltered = false;
                selectedSalesman.forEach(selectedSalesman => {
                    if (selectedSalesman.value === beatSalesman) {
                        isBeatFiltered = true;
                    }
                });
                if (isBeatFiltered) {
                    return true;
                }
                return false;
            });
        }
        if (selectedSalesCategories.length) {
            filteredBeats = filteredBeats.filter(beat => {
                const [beatCategory] = beat.categories;
                let isBeatFiltered = false;
                selectedSalesCategories.forEach(selectedCategory => {
                    if (selectedCategory.value === beatCategory) {
                        isBeatFiltered = true;
                    }
                });
                if (isBeatFiltered) {
                    return true;
                }
                return false;
            });
        }
        if (selectedWeeks.length) {
            filteredBeats = filteredBeats.filter(beat => {
                const beatWeek = beat.beatDay.week.toLowerCase();
                let isBeatFiltered = false;
                selectedWeeks.forEach(selectedWeek => {
                    if (selectedWeek.value.toLowerCase() === beatWeek) {
                        isBeatFiltered = true;
                    }
                });
                if (isBeatFiltered) {
                    return true;
                }
                return false;
            });
        }
        if (selectedDays.length) {
            filteredBeats = filteredBeats.filter(beat => {
                const beatDay = beat.beatDay.dayOfWeek.toLowerCase();
                let isBeatFiltered = false;
                selectedDays.forEach(selectedDay => {
                    if (selectedDay.value.toLowerCase() === beatDay) {
                        isBeatFiltered = true;
                    }
                });
                if (isBeatFiltered) {
                    return true;
                }
                return false;
            });
        }
        const updatedPaginationDetails = this.getDefaultPaginationDetails(
            filteredBeats
        );
        const selectedFilters = Map({
            days: selectedDays,
            weeks: selectedWeeks,
            salesCategories: selectedSalesCategories,
            salesman: selectedSalesman
        });
        const visibleBeats = this.getVisibleBeats(
            filteredBeats,
            updatedPaginationDetails
        );
        this.setState({
            filteredBeats: filteredBeats || [],
            visibleBeats,
            selectedFilters,
            paginationDetails: updatedPaginationDetails
        });
    };

    onPageChange = (nextPageIndex: number) => {
        const { paginationDetails, filteredBeats } = this.state;
        const { pageSize } = paginationDetails;
        let visibleBeats = [];
        const startIndex = pageSize * nextPageIndex;

        const updatedPaginatedDetails = {
            pageSize: paginationDetails.pageSize,
            totalItems: paginationDetails.totalItems,
            totalPages: paginationDetails.totalPages,
            selectedPage: nextPageIndex + 1,
            selectedPageIndex: nextPageIndex,
            pageSizeOptions: paginationDetails.pageSizeOptions
        };
        visibleBeats = filteredBeats.slice(startIndex, startIndex + pageSize);
        this.setState(
            {
                paginationDetails: updatedPaginatedDetails,
                visibleBeats
            },
            () => {
                this.resetSelectAll();
            }
        );
    };

    onPageSizeChange = (newPageSize: number) => {
        const { paginationDetails, beats, filteredBeats } = this.state;
        const { totalItems, pageSizeOptions } = paginationDetails;
        const totalPages = Math.ceil(beats.length / newPageSize);
        const startIndex = 0;
        const visibleBeats = filteredBeats.slice(
            startIndex,
            startIndex + newPageSize
        );
        const updatedPaginatedDetails = {
            pageSize: newPageSize,
            totalItems,
            totalPages,
            selectedPage: 1,
            selectedPageIndex: 0,
            pageSizeOptions
        };
        this.setState(
            () => {
                return {
                    paginationDetails: updatedPaginatedDetails,
                    visibleBeats
                };
            },
            () => {
                this.resetSelectAll();
            }
        );
    };

    getContainer = () => {
        return FpContainers.Allocated;
    };

    getContainerRef = (ref: ?typeof PlanMapWrap) => {
        this.allocatedContainerRef = ref;
    };

    getContainerRefValue = () => {
        return this.allocatedContainerRef;
    };

    getPlanMapWrapRef = (ref: ?typeof PlanMapWrap) => {
        this.planMapWrapRef = ref;
    };

    toggleMoveVisitDialog = () => {
        const { isMoveVisitDialogVisible } = this.state;
        if (isMoveVisitDialogVisible) {
            this.closeMoveVisitDialog();
            this.setState({
                targetVisitId: null,
                originalBeatId: null
            });
        }
    };

    closeMoveVisitDialog = () => {
        this.setState({
            isMoveVisitDialogVisible: false
        });
    };

    openMoveVisitDialog = (visitId: string, originalBeatId: string) => {
        this.setState({
            targetVisitId: visitId,
            isMoveVisitDialogVisible: true,
            originalBeatId
        });
    };

    getSalesmanOptions = () => {
        const { pjPlan } = this.state;
        const { ridersInfo } = pjPlan;
        const { riderIds } = ridersInfo;
        const riderOptions = riderIds.map(rider => {
            return {
                value: rider.riderId,
                label: rider.riderId
            };
        });
        return riderOptions;
    };

    onEditBeat = (targetBeatId: string, outletVisitId: string) => {
        const { moveVisit } = this.props;
        moveVisit({
            targetBeatId,
            outletVisitId,
            originalBeatId: this.state.originalBeatId
        });
    };

    onUnallocateVisit = (beatId: string, visitId: string) => {
        const { unallocateVisit } = this.props;
        const { visibleBeats } = this.state;
        const selectedBeat = visibleBeats.find(beat => {
            return beat.beatId === beatId;
        });
        const selectedVisit = selectedBeat.visits.find(visit => {
            return visit.outletVisitId === visitId;
        });
        const { outletVisitId } = selectedVisit;
        unallocateVisit({ outletVisitId, originalBeatId: beatId });
    };

    onSearchInputChange = (value: string) => {
        const { beats, paginationDetails } = this.state;
        const lowerCaseValue = value.toLowerCase();
        if (!value) {
            const visibleBeats = this.getVisibleBeats(beats, paginationDetails);
            this.setState({
                filteredBeats: beats,
                visibleBeats
            });
        }
        const { visitIdMap } = this.props;
        const filteredBeats = beats.filter(beat => {
            const beatId = beat.beatId.toLowerCase();
            const [categories] = beat.categories;
            const beatDay = beat.beatDay.dayOfWeek.toLowerCase();
            const week = beat.beatDay.week.toLowerCase();
            const currentSalesmanId = beat.currentSalesmanId.toLowerCase();
            const { visits } = beat;
            const isVisitSearched = visits.find(visit => {
                const { outletVisitId } = visit;
                if (outletVisitId) {
                    const outletData = visitIdMap.get(outletVisitId);
                    const sourceOutletId = outletData.sourceOutletId.toLowerCase();
                    if (sourceOutletId.includes(lowerCaseValue)) {
                        return visit;
                    }
                }
                return false;
            });
            if (
                isVisitSearched ||
                beatId.includes(lowerCaseValue) ||
                categories?.toLowerCase().includes(lowerCaseValue) ||
                beatDay.includes(lowerCaseValue) ||
                week.includes(lowerCaseValue) ||
                currentSalesmanId.includes(lowerCaseValue)
            ) {
                return beat;
            }
            return false;
        });
        const updatedPaginationDetails = this.getDefaultPaginationDetails(
            filteredBeats
        );
        const visibleBeats = this.getVisibleBeats(
            filteredBeats,
            paginationDetails
        );
        this.setState({
            filteredBeats,
            visibleBeats,
            paginationDetails: updatedPaginationDetails
        });
    };

    render() {
        const {
            beats,
            mapOptions,
            selectedBeat,
            paginationDetails,
            visibleBeats,
            pjPlan,
            beatSelectionMap,
            selectedVisit,
            contextMenuOpen,
            contextMenu,
            selectedSortOrder,
            selectedSortOption,
            salesCategoryColorMap,
            isMoveVisitDialogVisible,
            filteredBeats,
            selectedFilters,
            currentHoveredVisit,
            hoveredBeat,
            selectAll,
            status,
            unallocatedVisits,
            hoveredUnallocatedVisit,
            selectedUnallocatedVisit,
            targetVisitId,
            visibleBeatColorMap,
            riderIdColorMap,
            salesbeatPlan,
            weekColorMap,
            weekDayColorMap
        } = this.state;

        return (
            <FpAllocatedVisitsView
                {...this.props}
                status={status}
                toggleMoveVisitDialog={this.toggleMoveVisitDialog}
                onSelectAllToggle={this.onSelectAllToggle}
                isAllVisibleBeatsSelected={selectAll}
                isMoveVisitDialogVisible={isMoveVisitDialogVisible}
                onMouseLeave={this.onVisitHoverOutside}
                salesbeatPlan={salesbeatPlan}
                onVisitHover={this.onVisitHover}
                onVisitHoverOutside={this.onVisitHoverOutside}
                hoveredBeat={hoveredBeat}
                currentHoveredVisit={currentHoveredVisit}
                getAllFilters={this.getAllFilters}
                selectedFilters={selectedFilters}
                removeFilterItem={this.removeFilterItem}
                sortBeats={this.sortBeats}
                resetSort={this.resetSort}
                filteredBeats={filteredBeats}
                onSearchInputChange={this.onSearchInputChange}
                salesCategoryColorMap={salesCategoryColorMap}
                riderIdColorMap={riderIdColorMap}
                weekColorMap={weekColorMap}
                weekDayColorMap={weekDayColorMap}
                selectedSortOrder={selectedSortOrder}
                selectedSortOption={selectedSortOption}
                getSalesmanOptions={this.getSalesmanOptions}
                openMoveVisitDialog={this.openMoveVisitDialog}
                onUnallocateVisit={this.onUnallocateVisit}
                contextMenu={contextMenu}
                onContextMenuOpen={this.onContextMenuOpen}
                onContextMenuOpenMap={this.onContextMenuOpenMap}
                onUnallocatedVisitContextMenuOpen={
                    this.onUnallocatedVisitContextMenuOpen
                }
                onContextMenuClose={this.onContextMenuClose}
                contextMenuOpen={contextMenuOpen}
                onVisitClick={this.onVisitClick}
                onVisitDeselect={this.onVisitDeselect}
                selectedVisit={selectedVisit}
                pjPlan={pjPlan}
                beatSelectionMap={beatSelectionMap}
                applyFilters={this.applyFilters}
                targetVisit={targetVisitId}
                onClearFilter={this.onClearFilter}
                visibleBeatColorMap={visibleBeatColorMap}
                toggleBeatSelection={this.toggleBeatSelection}
                visibleBeats={visibleBeats}
                onPageChange={this.onPageChange}
                onEditBeat={this.onEditBeat}
                onPageSizeChange={this.onPageSizeChange}
                paginationDetails={paginationDetails}
                selectedBeat={selectedBeat}
                beats={beats}
                mapOptions={mapOptions}
                getContainerRefValue={this.getContainerRefValue}
                getPlanMapWrapRef={this.getPlanMapWrapRef}
                getContainer={this.getContainer}
                onOptionsChange={this.onOptionsChange}
                unallocatedVisits={unallocatedVisits}
                selectedUnallocatedVisit={selectedUnallocatedVisit}
                hoveredUnallocatedVisit={hoveredUnallocatedVisit}
                onUnallocatedVisitSelect={this.onUnallocatedVisitSelect}
                onUnallocatedVisitDeselect={this.onUnallocatedVisitDeselect}
                onUnallocatedVisitHover={this.onUnallocatedVisitHover}
                onUnallocatedVisitHoverOut={this.onUnallocatedVisitHoverOut}
            />
        );
    }
}

export default FpAllocatedVisitsContainer;
